'use client';

import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';

import { cn } from './lib/utils';

export type CalendarProps = DayPickerProps;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  numberOfMonths = 2,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      data-slot="calendar"
      showOutsideDays={showOutsideDays}
      numberOfMonths={numberOfMonths}
      className={cn('w-fit p-3', className)}
      classNames={{
        // `relative` is load-bearing: `nav` below is absolutely positioned,
        // so without a positioned ancestor here it escapes the calendar and
        // anchors to whatever container is positioned — the corners of the
        // whole dialog when the calendar sits inside a (fixed) DialogContent.
        // Its width matters too, hence `w-fit` on the root: the nav spans this
        // element, so the arrows only sit over the months if it is month-width.
        months: 'relative flex flex-col gap-4 sm:flex-row sm:gap-6',
        month: 'flex flex-col gap-3',
        month_caption: 'flex h-7 items-center justify-center px-8',
        caption_label: 'text-sm font-medium',
        nav: 'absolute inset-x-0 top-0 flex h-7 items-center justify-between px-1',
        button_previous: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-foreground/60 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-40',
        ),
        button_next: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-foreground/60 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-40',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-8 text-center text-[10px] font-medium uppercase tracking-wider text-muted-foreground',
        week: 'mt-1.5 flex w-full',
        week_number_header:
          'w-6 pr-1.5 text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60',
        week_number:
          'w-6 pr-1.5 text-right text-[11px] font-medium tabular-nums text-muted-foreground/70',
        day: 'relative h-8 w-8 p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button:
          'inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent font-normal text-foreground transition-colors hover:bg-muted aria-selected:opacity-100 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        today:
          'after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-primary',
        selected:
          '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
        outside: 'text-muted-foreground/40 aria-selected:text-muted-foreground',
        disabled: 'text-muted-foreground/40 opacity-50',
        hidden: 'invisible',
        range_start:
          '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:rounded-md',
        range_end: '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:rounded-md',
        range_middle:
          'bg-primary/15 [&>button]:bg-transparent [&>button]:text-foreground [&>button]:hover:bg-primary/25',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName, ...rest }) => {
          const icon = orientation === 'right' ? ArrowRight01Icon : ArrowLeft01Icon;
          return (
            <HugeiconsIcon
              icon={icon}
              aria-hidden
              className={cn('size-4', chevronClassName)}
              {...rest}
            />
          );
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
